import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Pin, Trash2, Ban, X } from "lucide-react";

/**
 * LiveChatFeed — comment feed for a live stream. Polls
 * GET /live/{id}/chat (since=<last created_at>) so the host (inner screen)
 * and every viewer (outer screen) see the same comments, and
 * GET /live/{id}/chat-state for the pinned comment + host-deleted ids.
 *
 * isHost: tapping a comment opens Pin / Delete / Block (support #273638).
 * hostUserId: the host's own comments get Pin / Delete only.
 */
export default function LiveChatFeed({ streamId, className = "", refreshKey = 0, isHost = false, hostUserId = null }) {
  const [items, setItems] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [deleted, setDeleted] = useState(() => new Set());
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const sinceRef = useRef(null);

  useEffect(() => {
    if (!streamId) return undefined;
    let alive = true;
    sinceRef.current = null;
    setItems([]);
    const tick = async () => {
      try {
        const params = { limit: 30 };
        if (sinceRef.current) params.since = sinceRef.current;
        const [r, st] = await Promise.all([
          api.get(`/live/${streamId}/chat`, { params }),
          api.get(`/live/${streamId}/chat-state`).catch(() => null),
        ]);
        if (!alive) return;
        const rows = r.data || [];
        if (rows.length) {
          sinceRef.current = rows[rows.length - 1].created_at;
          setItems((prev) => {
            const seen = new Set(prev.map((x) => x.id));
            return [...prev, ...rows.filter((x) => !seen.has(x.id))].slice(-30);
          });
        }
        if (st?.data) {
          setPinned(st.data.pinned || null);
          setDeleted(new Set(st.data.deleted_ids || []));
        }
      } catch { /* polling is best-effort */ }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { alive = false; clearInterval(t); };
  }, [streamId, refreshKey]);

  const act = async (kind, c) => {
    if (busy) return;
    if (kind === "block" && !window.confirm(`Block @${c.username || "user"} from commenting on this live? All their comments will be removed.`)) return;
    setBusy(true);
    try {
      if (kind === "pin") {
        const r = await api.post(`/live/${streamId}/chat/${c.id}/pin`);
        setPinned(r.data?.pinned || null);
      } else if (kind === "unpin") {
        await api.delete(`/live/${streamId}/pin`);
        setPinned(null);
      } else if (kind === "delete") {
        await api.delete(`/live/${streamId}/chat/${c.id}`);
        setDeleted((d) => new Set([...d, c.id]));
        if (pinned?.id === c.id) setPinned(null);
      } else if (kind === "block") {
        await api.post(`/live/${streamId}/block/${c.user_id}`);
        setItems((prev) => prev.filter((x) => x.user_id !== c.user_id));
        if (pinned?.user_id === c.user_id) setPinned(null);
      }
      setActive(null);
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Action failed, please try again");
    } finally { setBusy(false); }
  };

  const visible = items.filter((x) => !deleted.has(x.id)).slice(-6);
  if (!visible.length && !pinned) return null;
  return (
    <div className={`${isHost ? "pointer-events-auto" : "pointer-events-none"} overflow-hidden flex flex-col justify-end gap-1 ${className}`} data-testid="live-chat-feed">
      {pinned && (
        <div className="max-w-full w-fit rounded-2xl bg-[#FF7A00]/25 border border-[#FF7A00]/60 backdrop-blur px-2.5 py-1 text-xs text-white flex items-start gap-1.5" data-testid="live-chat-pinned">
          <Pin className="w-3 h-3 mt-0.5 shrink-0 text-[#FF7A00]"/>
          <span className="min-w-0"><b className="text-[#FF7A00]">@{pinned.username || "user"}</b> <span className="break-words">{pinned.text}</span></span>
          {isHost && (
            <button onClick={() => act("unpin")} className="shrink-0 ml-1 text-white/70" data-testid="live-chat-unpin" aria-label="Unpin"><X className="w-3.5 h-3.5"/></button>
          )}
        </div>
      )}
      {visible.map((c) => (
        <div key={c.id} className="max-w-full w-fit">
          <div
            onClick={isHost ? () => setActive(active === c.id ? null : c.id) : undefined}
            className={`rounded-2xl bg-black/55 backdrop-blur px-2.5 py-1 text-xs text-white ${isHost ? "cursor-pointer active:bg-black/75" : ""}`}
            data-testid="live-chat-item">
            <b className="text-[#00E5FF]">@{c.username || "user"}</b> <span className="break-words">{c.text}</span>
          </div>
          {isHost && active === c.id && (
            <div className="mt-1 flex gap-1.5" data-testid="live-chat-actions">
              <button disabled={busy} onClick={() => act("pin", c)} className="px-2 py-1 rounded-full bg-[#FF7A00] text-black text-[11px] font-bold flex items-center gap-1" data-testid="live-chat-pin"><Pin className="w-3 h-3"/>Pin</button>
              <button disabled={busy} onClick={() => act("delete", c)} className="px-2 py-1 rounded-full bg-white/90 text-black text-[11px] font-bold flex items-center gap-1" data-testid="live-chat-delete"><Trash2 className="w-3 h-3"/>Delete</button>
              {c.user_id && c.user_id !== hostUserId && (
                <button disabled={busy} onClick={() => act("block", c)} className="px-2 py-1 rounded-full bg-[#FF2A5F] text-white text-[11px] font-bold flex items-center gap-1" data-testid="live-chat-block"><Ban className="w-3 h-3"/>Block</button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
